/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound } from 'lucide-react';
import { BoardColumn } from '@/shared/ui/components/basic/board';
import {
  LEASE_BOARD_COLUMN_STYLES,
  groupLeasesByColumn,
  useLeaseBoardColumnLabel,
} from '../constants/lease-board-columns';
import { LeaseBoardCard } from './LeaseBoardCard';
import { matchesLeaseTriage } from '../constants/lease-triage';
import type { Lease } from '../types/lease.types';

export interface LeaseBoardProps {
  agencyId: string;
  /** The leases the page already filtered and searched. */
  leases: readonly Lease[];
  tenantNameOf: (lease: Lease) => string;
  /** Null while the property's label is still loading. */
  propertyLabelOf: (lease: Lease) => string | null;
}

/**
 * Locações as a board: one column per phase of the contract's life.
 *
 * Unpaged on purpose. The page fetches every lease of the agency in one
 * call and pages the *table* in the browser, so the board can show all of
 * them without asking for anything more — and a column that hid rows
 * behind a "load more" would be lying about the count in its own header.
 * If the fetch ever becomes paged, the counts here have to come from the
 * server the way the attendance board's do.
 */
/**
 * The warning under a column heading: how many of its leases have stood
 * still for a week.
 *
 * Honest because the board is unpaged — the column holds every lease of
 * its phase, so this counts them all rather than a loaded slice.
 *
 * Silent at zero. A column that is entirely up to date should say
 * nothing; printing "0 parados" turns good news into another line to read.
 *
 * @param leases - The column's leases.
 * @param t - The namespace translator.
 * @returns The warning, or undefined when nothing is stalled.
 */
function alertFor(
  leases: readonly Lease[],
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  const stalled = leases.filter((lease) => matchesLeaseTriage(lease, 'stalled')).length;
  return stalled > 0 ? t('board.column.stalled', { count: stalled }) : undefined;
}

/**
 * LeaseBoard component.
 */
export function LeaseBoard({
  agencyId, leases, tenantNameOf, propertyLabelOf,
}: LeaseBoardProps) {
  const { t } = useTranslation('features/leases');
  const columnLabel = useLeaseBoardColumnLabel();
  const columns = useMemo(() => groupLeasesByColumn(leases), [leases]);

  return (
    <div
      data-testid="lease-board"
      className="flex flex-col gap-(--space-5) md:grid md:items-start md:gap-(--space-4)"
      style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr)` }}
    >
      {columns.map((column) => (
        <BoardColumn
          key={column.id}
          id={column.id}
          label={columnLabel(column.id)}
          count={column.leases.length}
          accent={LEASE_BOARD_COLUMN_STYLES[column.id]}
          emptyIcon={(
            <KeyRound width={18} height={18} style={{ color: 'var(--text-tertiary)', opacity: 0.6 }} />
          )}
          emptyLabel={t('board.column.empty')}
          isEmpty={column.leases.length === 0}
          alert={alertFor(column.leases, t)}
        >
          {column.leases.map((lease) => (
            <LeaseBoardCard
              key={lease.id}
              agencyId={agencyId}
              lease={lease}
              tenantName={tenantNameOf(lease)}
              propertyLabel={propertyLabelOf(lease)}
            />
          ))}
        </BoardColumn>
      ))}
    </div>
  );
}

export default LeaseBoard;
